"use client";

import {useState} from "react";
import Link from "next/link";
import {ChevronLeft, ChevronRight} from "lucide-react";
import {Button} from "@/components/ui/button";
import {Carousel, CarouselContent, CarouselItem, type CarouselApi} from "@/components/ui/carousel";
import {CarouselDots, useAutoplayPlugins} from "@/components/carousel-extras";
import {ClassicListingCard, type ClassicCardData} from "./classic-card";

/** Outbound search of all listings on realtor.ca for Chilliwack, BC (owner decision Q7b). */
export const SEARCH_ALL_LISTINGS_URL = "https://www.realtor.ca/bc/chilliwack/real-estate";

/** Seconds between automatic advances, in milliseconds. */
const AUTOPLAY_MS = 10000;

/**
 * The home page's Listings section in the classic look: the heading, a carousel of classic cards
 * (three across on a desktop, two on a tablet, one on a phone, autoplay every 10 seconds unless the
 * visitor prefers reduced motion, dots below), and under it the "Search all
 * listings" button and the Terms of use line.
 *
 * The previous and next buttons sit in their own gutters left and right of the carousel, so they
 * never cover any part of a card (CREA's rule 3(d)); the carousel has no arrows over the cards. `searchHref` is `internal` for our own listing search and `realtor` for
 * the outbound search on realtor.ca (new tab). `termsLine` adds the one-line
 * Terms of use note (office-feed listings). With no cards the carousel is left out and the button
 * remains.
 */
export function ClassicListingCarousel({cards, searchHref, termsLine}: {
    cards: ClassicCardData[];
    searchHref: "internal" | "realtor";
    termsLine?: boolean;
}) {
    const [api, setApi] = useState<CarouselApi>();
    const plugins = useAutoplayPlugins(AUTOPLAY_MS);
    return (
        <div id="listings" className="bg-white px-2 md:px-6 pb-10 scroll-m-20">
            <h2 className="text-4xl text-center p-5">Listings</h2>

            {cards.length > 0 ? (
                <div className="flex items-center gap-1 md:gap-2">
                    <Button type="button" variant="outline" size="icon" className="shrink-0"
                            onClick={() => api?.scrollPrev()} aria-label="Previous listings">
                        <ChevronLeft aria-hidden="true"/>
                    </Button>
                    <div className="min-w-0 flex-1">
                        <Carousel setApi={setApi} plugins={plugins} opts={{loop: true, align: "start", slidesToScroll: "auto"}}
                                  aria-label="Featured listings" className="pb-2 md:leading-loose">
                            <CarouselContent className="ml-0">
                                {cards.map((card) => (
                                    <CarouselItem key={card.key} className="p-3 min-[464px]:basis-1/2 lg:basis-1/3">
                                        <div className="h-full">
                                            <ClassicListingCard card={card}/>
                                        </div>
                                    </CarouselItem>
                                ))}
                            </CarouselContent>
                        </Carousel>
                        <CarouselDots api={api} className="pb-8 pt-2"/>
                    </div>
                    <Button type="button" variant="outline" size="icon" className="shrink-0"
                            onClick={() => api?.scrollNext()} aria-label="Next listings">
                        <ChevronRight aria-hidden="true"/>
                    </Button>
                </div>
            ) : null}

            <div className="flex flex-col items-center gap-4 pt-4">
                {searchHref === "internal" ? (
                    <Button asChild variant="brand">
                        <Link href="/listings" prefetch={false}>Search all listings</Link>
                    </Button>
                ) : (
                    <Button asChild variant="brand">
                        <a href={SEARCH_ALL_LISTINGS_URL} target="_blank" rel="noopener noreferrer">
                            Search all listings
                            <span className="sr-only"> on realtor.ca (opens in a new tab)</span>
                        </a>
                    </Button>
                )}
                {cards.length > 0 && termsLine ? (
                    <p className="text-center text-sm">
                        Listing information is provided under the{" "}
                        <Link href="/terms" prefetch={false} className="underline underline-offset-2 hover:no-underline">
                            Terms of use
                        </Link>.
                    </p>
                ) : null}
            </div>
        </div>
    );
}
